/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { push } from '@lagunovsky/redux-react-router';
import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { groupInternalHighscorePath } from '.';
import { loadWithoutPropsTournamentRoundForwarder } from '../../reactUtils';
import { GroupInternalHighscore } from '../../services/betting';
import { fetchWrapperFunc, fetchWrapperFuncNoLoading } from '../../utils';
import { isLoggedInSelector } from '../System/selectors';
import { getGroupHighscoreApi2, getSystemConfig, getTournamentApi2, getUserGroupApi2 } from '../System/systemConfig';
import {
    tournamentSelectionChangeTournamentRound,
    tournamentSelectionVerifyAndAdjustSelectedTournamentByTrId
} from '../TournamentSelectionComponent/actions';
import { tournamentSelectionSelectedTournamentRoundIdSelector } from '../TournamentSelectionComponent/selectors';
import {
    gihsSetAvailableGroups,
    gihsSetColumnsEnabled,
    gihsSetGroupDetails,
    gihsSetIsLoading,
    gihsSetStartColumn,
    GIHS_COLUMN_LEFT,
    GIHS_COLUMN_RIGHT,
    GIHS_LOAD_WITHOUT_PROPS,
    GIHS_LOAD_WITH_PROPS,
    GIHS_SET_COLUMN_COUNT,
    GIHS_TOURNAMENT_ID_CHANGED,
    GIHS_TR_ID_CHANGED,
    GroupInternalHighscoreParams,
    loadGroupInternalHighscores,
    LOAD_GROUP_INTERNAL_HIGHSCORES,
    setGroupInternalHighscores,
    setGroupInternalUserGroupId
} from './actions';
import {
    gihsColumnCountSelector,
    gihsEntitySelector,
    gihsStartColumnSelector,
    gihsUserGroupIdSelector
} from './selectors';
import { myProfileBettingPath } from '../../screens/UserProfileScreen';

function* loadGroupInternalHighscoreGenerator(action: Action<GroupInternalHighscoreParams>) {
    const groupHighscoreApi = getGroupHighscoreApi2();
    const ugId = action.payload.ugId;
    const trId = action.payload.trId;

    yield put(gihsSetIsLoading(true));

    try {
        yield* fetchWrapperFunc(
            () => groupHighscoreApi.getGroupInternalHighscores({clientName: getSystemConfig().clientName, trId: Number(trId), ugId: Number(ugId)}),
            [r => setGroupInternalHighscores(r)]
        );

        yield* fetchWrapperFuncNoLoading(() => getUserGroupApi2().getDetailOfUserGroup({ugId: Number(ugId)}), [
            r => gihsSetGroupDetails(r)
        ]);

        const tr = yield* fetchWrapperFuncNoLoading(() => getTournamentApi2().getTournamentRoundById({trId: Number(trId)}));
        const isLoggedIn: boolean = yield select(isLoggedInSelector);

        if (tr && tr.tournamentId && isLoggedIn) {
            yield* fetchWrapperFuncNoLoading(
                () => getUserGroupApi2().getUserGroupsParticipating({tId: String(tr.tournamentId)}),
                [r => gihsSetAvailableGroups(r)]
            );
        } else {
            yield put(gihsSetAvailableGroups([]));
        }
        yield* refreshColumnButtonEnablement();
    } finally {
        yield put(gihsSetIsLoading(false));
    }
}

function* columnLeft() {
    const currentStartColumn: number = yield select(gihsStartColumnSelector);
    if (currentStartColumn > 0) {
        yield put(gihsSetStartColumn(currentStartColumn - 1));
    }
    yield* refreshColumnButtonEnablement();
}

function* columnRight() {
    const currentStartColumn: number = yield select(gihsStartColumnSelector);
    const currentColumnCount: number = yield select(gihsColumnCountSelector);
    const highscores: GroupInternalHighscore = yield select(gihsEntitySelector);

    const isLeaf = highscores.games && highscores.games.length > 0;
    const maxValue = isLeaf ? highscores.games.length : highscores.childTournamentRounds.length;
    if (currentStartColumn + currentColumnCount < maxValue) {
        yield put(gihsSetStartColumn(currentStartColumn + 1));
    }

    yield* refreshColumnButtonEnablement();
}

function* setColumnCount() {
    yield* refreshColumnButtonEnablement();
}

function* refreshColumnButtonEnablement() {
    const currentStartColumn: number = yield select(gihsStartColumnSelector);
    const currentColumnCount: number = yield select(gihsColumnCountSelector);
    const highscores: GroupInternalHighscore = yield select(gihsEntitySelector);
    if (highscores) {
        // decide if it is a Node or Leaf round
        const isLeaf = highscores.games && highscores.games.length > 0;
        const maxValue = isLeaf ? highscores.games.length : highscores.childTournamentRounds.length;
        const leftEnabled = currentStartColumn > 0;
        if (currentStartColumn + currentColumnCount > maxValue) {
            // make sure that we show the max number of columns (can happen after screen resize)
            yield put(gihsSetStartColumn(maxValue - currentColumnCount > 0 ? maxValue - currentColumnCount : 0));
        }
        const rightEnabled = currentStartColumn + currentColumnCount < maxValue;
        yield put(gihsSetColumnsEnabled({ leftEnabled, rightEnabled }));
    }
}

function* trIdChanged(action: Action<string>) {
    const trId = action.payload;
    const ugId: number = yield select(gihsUserGroupIdSelector);
    if (ugId) {
        yield put(push(groupInternalHighscorePath(trId, String(ugId))));
        yield put(gihsSetStartColumn(0));
    }
}

function* tournamentIdChanged(action: Action<string>) {
    const ugId: number = yield select(gihsUserGroupIdSelector);
    if (ugId) {
        yield* loadWithoutPropsTournamentRoundForwarder('highscores', (trId: string) =>
            groupInternalHighscorePath(trId, String(ugId))
        );
    }
}

function* loadWithProps(action: Action<GroupInternalHighscoreParams>) {
    const params = action.payload;
    yield put(setGroupInternalUserGroupId(Number(params.ugId)));
    if (params.trId) {
        yield put(
            tournamentSelectionChangeTournamentRound({
                type: 'highscores',
                trId: params.trId
            })
        );
        yield put(tournamentSelectionVerifyAndAdjustSelectedTournamentByTrId(String(params.trId)));
        yield put(loadGroupInternalHighscores(params));
    } else {
        yield put(push('/'));
    }
}

function* loadWithoutProps() {
    // if the user is not logged in, go to index page
    const isLoggedIn: boolean = yield select(isLoggedInSelector);

    if (!isLoggedIn) {
        yield put(push('/'));
    } else {
        const trId: string | undefined = yield select(tournamentSelectionSelectedTournamentRoundIdSelector('highscores'));
        const ug = yield* fetchWrapperFuncNoLoading(() => getUserGroupApi2().getDefault());
        if (!ug) {
            // user doesn't have a user group; maybe forwarding to the profile page?
            yield put(push(myProfileBettingPath())); 
        } else {
            if (!trId) {
                // if no tournament round is selected ... forward to the default round
                yield* loadWithoutPropsTournamentRoundForwarder('highscores', trId =>
                    groupInternalHighscorePath(trId, String(ug.id))
                );
            } else {
                if (trId && ug) {
                    yield put(push(groupInternalHighscorePath(trId, String(ug.id))));
                }
            }
        }
    }
}

export function* groupInternalHighscoreSagas() {
    yield takeLatest(LOAD_GROUP_INTERNAL_HIGHSCORES, loadGroupInternalHighscoreGenerator);
    yield takeEvery(GIHS_COLUMN_LEFT, columnLeft);
    yield takeEvery(GIHS_COLUMN_RIGHT, columnRight);
    yield takeEvery(GIHS_SET_COLUMN_COUNT, setColumnCount);
    yield takeLatest(GIHS_TR_ID_CHANGED, trIdChanged);
    yield takeLatest(GIHS_TOURNAMENT_ID_CHANGED, tournamentIdChanged);
    yield takeLatest(GIHS_LOAD_WITH_PROPS, loadWithProps);
    yield takeLatest(GIHS_LOAD_WITHOUT_PROPS, loadWithoutProps);
}
